import { type ReactNode } from 'react'

import { PaneListSkeleton } from '@/components/layout/pane-list-skeleton'
import { cn } from '@/lib/utils'

export type PaneListSectionProps = {
  /** Section label before the count, e.g. "Organisations", "Jobs". */
  title: string
  /** Omit while loading so the heading does not show a misleading total. */
  count?: number
  /** Optional leading icon (e.g. same size as Filters card titles). */
  icon?: ReactNode
  /** When false, only render children (e.g. toolbar carries the title). */
  showHeading?: boolean
  children: ReactNode
  className?: string
}

/**
 * Shared heading + body shell for employer list panes (organisations, jobs).
 * Keeps title / count typography consistent with card section headers.
 */
export function PaneListSection({
  title,
  count,
  icon,
  showHeading = true,
  children,
  className,
}: PaneListSectionProps) {
  return (
    <div className={cn('space-y-4', className)}>
      {showHeading ? (
        <h2 className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 leading-none font-semibold">
          {icon ? (
            <span className="flex size-4 shrink-0 items-center justify-center [&_svg]:size-4">
              {icon}
            </span>
          ) : null}
          <span className="min-w-0">{title}</span>
          {typeof count === 'number' ? (
            <span className="shrink-0 text-xs font-normal tabular-nums text-muted-foreground/90">
              · {count}
            </span>
          ) : null}
        </h2>
      ) : null}
      {children}
    </div>
  )
}

export type PaneListErrorProps = { message: string; className?: string }

export function PaneListError({ message, className }: PaneListErrorProps) {
  return (
    <div
      className={cn(
        'flex min-h-[12rem] items-center justify-center px-4 py-12 text-center text-destructive',
        className,
      )}
      role="alert"
    >
      <p>{message}</p>
    </div>
  )
}

export type PaneListLoadingProps = { message: string; className?: string }

/** Shared loading block for employer pane lists — content-shaped skeleton. */
export function PaneListLoading({ message, className }: PaneListLoadingProps) {
  return (
    <PaneListSkeleton
      ariaLabel={message}
      className={className}
    />
  )
}
